/*
Copyright (C) 2022  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import React from "react";
import styled from "styled-components";
import { observer } from "mobx-react";
import { Fingerprint, Plus } from "lucide-react";

import CopyValue from "@src/components/ui/CopyValue";
import InfoIcon from "@src/components/ui/InfoIcon";
import { T9 } from "@src/components/ui/Lists/ListTable";
import { PrimaryButton } from "@src/components/ui/Lists/EmptyState/EmptyState";

// A strip under the toolbar: the on-prem hub's fingerprint (for installing the agent) and "Add".
const Wrapper = styled.div<{ visible: boolean }>`
  display: ${props => (props.visible ? "flex" : "none")};
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  background: ${T9.slate50};
  border-bottom: 1px solid ${T9.slate200};
  font-size: 13px;
  color: ${T9.slate600};
  flex-shrink: 0;
`;
const FingerPrint = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  svg.fp {
    color: ${T9.slate400};
    flex-shrink: 0;
  }
`;
const FingerPrintLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${T9.slate500};
  white-space: nowrap;
`;
const FingerPrintValue = styled.div`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  color: ${T9.slate800};
  min-width: 0;
  overflow: hidden;
`;

type Props = {
  hideButton: boolean;
  error: string;
  fingerprint: string;
  visible: boolean;
  onCreateClick: () => void;
};

@observer
class MetalHubListHeader extends React.Component<Props> {
  renderContent() {
    let valueContent: React.ReactNode = this.props.error;

    if (!this.props.error) {
      const cleanFingerprint = this.props.fingerprint.replace(/\n/g, "");
      const fingerprintShortened = `${cleanFingerprint.slice(
        0,
        12,
      )}…${cleanFingerprint.slice(-12)}`;
      valueContent = cleanFingerprint ? (
        <CopyValue value={cleanFingerprint} label={fingerprintShortened} />
      ) : (
        "Loading…"
      );
    }
    return (
      <FingerPrint>
        <Fingerprint className="fp" size={16} />
        <FingerPrintLabel>
          On-prem hub fingerprint
          <InfoIcon text="Enter this fingerprint when you install the SwiftIQ agent on an on-prem server" />
        </FingerPrintLabel>
        <FingerPrintValue>{valueContent}</FingerPrintValue>
      </FingerPrint>
    );
  }

  render() {
    return (
      <Wrapper visible={this.props.visible} data-test-id="metalHubListHeader">
        {this.renderContent()}
        {!this.props.hideButton ? (
          <PrimaryButton type="button" secondary onClick={this.props.onCreateClick}>
            <Plus size={16} />
            Add on-prem server
          </PrimaryButton>
        ) : null}
      </Wrapper>
    );
  }
}

export default MetalHubListHeader;
